import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, formatDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Coffee, CheckCircle } from "@phosphor-icons/react";

const GROUPS = [
  ["brotscheiben", "Brötchen & Backwaren", ["Brotscheiben","Kaiserbrötchen","Vollkornbrötchen","Croissant","Laugengebäck"]],
  ["eier", "Eier", ["Gekochtes Ei hart","Gekochtes Ei weich","Rührei","Spiegelei"]],
  ["suesses", "Süßes & Aufstriche", ["Butter","Honig","Erdbeer Marmelade","Pfirsich Marmelade","Pflaumen Marmelade","Waldfrucht Marmelade","Nutella"]],
  ["muesli", "Müsli & Joghurt", ["Naturjoghurt","Fruchtjoghurt","Cornflakes","Müsli"]],
  ["kaese_wurst", "Käse & Wurst / Aufschnitt", ["Frischkäse Natur","Camembert","Schnittkäse","Gekochter Schinken","Roher Schinken","Aufschnitt","Leberwurst","Salami","Teewurst"]],
  ["extras", "Extras", ["Tomate","Gurke","Avocado","Frisches Obst"]],
  ["getraenke", "Getränke", ["Kaffee","Kaffee mit Milch","Tee","Espresso","Cappuccino","Orangensaft","Kakao warm","Kakao kalt","Wasser still","Wasser sprudel"]],
];

export default function Breakfast() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [form, setForm] = useState({
    name: "", room_note: "", date: "", time: "08:30",
    brotscheiben: [], eier: [], suesses: [], muesli: [],
    kaese_wurst: [], extras: [], getraenke: [], besondere_wuensche: "",
  });

  useEffect(() => {
    axios.get(`${API}/public/guest/${token}`).then(r => {
      setCtx(r.data);
      setForm(f => ({
        ...f,
        name: `${r.data.guest.first_name} ${r.data.guest.last_name}`.trim(),
        room_note: r.data.booking.room,
        date: r.data.booking.check_in,
      }));
    }).catch(e => setErr(e.response?.data?.detail || "Ungültiger Link"));
  }, [token]);

  const toggle = (group, option) => {
    setForm(f => {
      const has = f[group].includes(option);
      return { ...f, [group]: has ? f[group].filter(x => x !== option) : [...f[group], option] };
    });
  };

  const submit = async () => {
    try {
      await axios.post(`${API}/public/breakfast/${token}`, form);
      setOk(true);
    } catch (e) { setErr(e.response?.data?.detail || "Fehler beim Absenden"); }
  };

  if (err && !ctx) return <div className="min-h-screen flex items-center justify-center p-6"><div className="msb-card max-w-md">{err}</div></div>;
  if (!ctx) return null;
  if (ok) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="msb-card max-w-md text-center">
        <CheckCircle size={64} weight="duotone" className="mx-auto text-primary"/>
        <h2 className="text-2xl font-bold mt-3" style={{fontFamily:"'Work Sans',sans-serif"}}>Frühstück bestellt</h2>
        <p className="text-muted-foreground mt-2">Wir freuen uns auf Sie am {formatDate(form.date)} um {form.time} Uhr.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-10 px-4 bg-background">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-primary">
            <Coffee size={28} weight="duotone"/>
            <span className="font-bold text-lg" style={{fontFamily:"'Work Sans',sans-serif"}}>MainSpessartBlick</span>
          </div>
          <h1 className="text-3xl font-bold mt-2" style={{fontFamily:"'Work Sans',sans-serif"}}>Frühstücks-Checkliste</h1>
          <p className="text-muted-foreground mt-1">🥐 19,99 € pro Person · frisch, regional & bio · 7:00 – 9:00 Uhr</p>
        </div>

        <div className="msb-card space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} className="rounded-xl mt-1.5" data-testid="bf-name"/></div>
            <div><Label>Zimmer / Einheit</Label><Input value={form.room_note} onChange={e=>setForm({...form, room_note: e.target.value})} className="rounded-xl mt-1.5"/></div>
            <div><Label>Datum</Label><Input type="date" value={form.date} onChange={e=>setForm({...form, date: e.target.value})} className="rounded-xl mt-1.5" data-testid="bf-date"/></div>
            <div><Label>Uhrzeit</Label><Input type="time" value={form.time} onChange={e=>setForm({...form, time: e.target.value})} className="rounded-xl mt-1.5"/></div>
          </div>

          {GROUPS.map(([key, label, options]) => (
            <div key={key}>
              <div className="font-semibold text-sm mb-2" style={{fontFamily:"'Work Sans',sans-serif"}}>{label}</div>
              <div className="grid grid-cols-2 gap-2">
                {options.map(o => (
                  <label key={o} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-muted/40 cursor-pointer" data-testid={`bf-${key}-${o}`}>
                    <Checkbox checked={form[key].includes(o)} onCheckedChange={()=>toggle(key, o)}/>
                    <span>{o}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div>
            <Label>Besondere Wünsche / Zusatzinformationen</Label>
            <Textarea value={form.besondere_wuensche} onChange={e=>setForm({...form, besondere_wuensche: e.target.value})} className="rounded-xl mt-1.5"/>
          </div>

          {err && <div className="text-sm text-destructive">{err}</div>}

          <Button onClick={submit} className="w-full h-11 rounded-xl bg-primary" data-testid="bf-submit">
            Bestellung absenden
          </Button>
        </div>
      </div>
    </div>
  );
}
